import React, { useState } from 'react';
import {
  Table as AntTable,
  Button as AntButton,

  Space,
  Typography as AntTypography,
  Tooltip,
} from 'antd';
import type { TableProps as AntTableProps } from 'antd';
import type { ColumnType as AntColumnType } from 'antd/es/table/interface';
import {
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Interface for column definition, adapted for Ant Design
// T is the type of the data record
export interface AntDColumn<T extends object> { // No longer extends AntColumnType directly
  key: React.Key;
  title: React.ReactNode;
  dataIndex?: keyof T | (string | number)[]; // Adjusted for better compatibility with AntD DataIndex
  render?: (value: any, record: T, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  fixed?: 'left' | 'right' | boolean;
  // Add other AntD specific column props if needed, e.g., sorter, filters
}

// Interface for DataTable props
export interface DataTableProps<T extends object> { // T must be an object for AntD Table records
  columns: AntDColumn<T>[];
  dataSource: T[];
  loading?: boolean;
  rowKey: keyof T | ((record: T) => React.Key); // Adjusted rowKey type
  className?: string; // Add className prop for tour targeting
  
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  onView?: (record: T) => void;
  onBulkDelete?: (selectedRowKeys: React.Key[], selectedRows: T[]) => void;
  onBulkUpload?: () => void;
  
  headerActions?: React.ReactNode;
  onRowClick?: (record: T, index?: number, event?: React.MouseEvent<HTMLElement>) => void;
  scroll?: AntTableProps<T>['scroll']; // Optional: if scroll control is needed
  
  // Pagination props
  pagination?: {
    current?: number;
    pageSize?: number;
    total?: number;
    onChange?: (page: number, pageSize?: number) => void;
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    pageSizeOptions?: string[];
    showTotal?: (total: number, range: [number, number]) => string;
  } | false;
}

export const DataTable = <T extends object>({
  columns,
  dataSource,
  loading,
  rowKey,
  className = "data-table",
  onEdit,
  onDelete,
  onView,
  onBulkDelete,
  onBulkUpload,
  headerActions,
  onRowClick,
  scroll,
  pagination,
}: DataTableProps<T>) => {
  const { t } = useTranslation(['common']);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<T[]>([]);

  const onSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: T[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(newSelectedRows);
  };

  const rowSelectionConfig = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  // Prepare columns for AntD Table, including an actions column if needed
  const antdTableColumns = [...columns];

  if (onEdit || onDelete || onView) {
    antdTableColumns.push({
      key: 'actions',
      title: t('common:ui.actions'),
      align: 'right',
      width: 100,
      fixed: 'right',
      render: (_: any, record: T) => (
        <Space className="record-actions" size="middle">
          {onView && (
            <Tooltip title={t('common:ui.view')}>
              <AntButton 
                type="text" 
                icon={<EyeOutlined />} 
                onClick={(e) => { e.stopPropagation(); onView(record); }}
              />
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title={t('common:ui.edit')}>
              <AntButton 
                type="text" 
                icon={<EditOutlined />} 
                onClick={(e) => { e.stopPropagation(); onEdit(record); }}
              />
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t('common:ui.delete')}>
              <AntButton 
                type="text" 
                danger 
                icon={<DeleteOutlined />} 
                onClick={(e) => { e.stopPropagation(); onDelete(record); }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    });
  }

  return (
    <div className="data-table-container" style={{ width: '100%' }}>
      <Space className="table-actions" style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Space>
          {selectedRowKeys.length > 0 && (
            <AntTypography.Text>
              {t('common:ui.selectedNItems', { count: selectedRowKeys.length })}
            </AntTypography.Text>
          )}
          {selectedRowKeys.length > 0 && onBulkDelete && (
            <AntButton
              className="bulk-delete-button"
              danger
              onClick={() => onBulkDelete(selectedRowKeys, selectedRows)}
              icon={<DeleteOutlined />}
            >
              {t('common:ui.bulkDelete')}
            </AntButton>
          )}
        </Space>
        <Space>
          {headerActions}
          {onBulkUpload && (
            <AntButton className="bulk-upload-button" onClick={onBulkUpload} icon={<UploadOutlined />}>
              {t('common:ui.bulkUpload')}
            </AntButton>
          )}
        </Space>
      </Space>

      <AntTable<T>
        className={className}
        rowKey={rowKey}
        columns={antdTableColumns as AntColumnType<T>[]}
        dataSource={dataSource}
        loading={loading}
        rowSelection={onBulkDelete || selectedRowKeys.length > 0 ? rowSelectionConfig : undefined}
        pagination={pagination}
        onRow={(record, rowIndex) => ({
          onClick: (event) => {
            if (onRowClick) {
                let target = event.target as HTMLElement;
                let isActionClick = false;
                while (target && target !== event.currentTarget) {
                    if (target.tagName === 'BUTTON' || target.closest('.ant-btn')) {
                        isActionClick = true;
                        break;
                    }
                    target = target.parentNode as HTMLElement;
                }
                if (!isActionClick) {
                    onRowClick(record, rowIndex, event);
                }
            }
          },
        })}
        scroll={scroll}
        bordered
        size="small"
      />
    </div>
  );
}; 